import React, { useState, useMemo, memo } from "react";
import { LordIcon, LordIconProps } from "./lord-icon";

export type LordIconRowProps = LordIconProps & {
  rowColor?: string; // Color to use when row is hovered
  rowStroke?: number; // Stroke thickness to use when row is hovered
  isRowHover?: boolean; // Whether the row is being hovered
  style?: React.CSSProperties; // Additional style properties
};

/**
 * LordIconRow - A wrapper around LordIcon that adds row-specific functionality
 * This component is used for icons in table rows that need special hover behavior
 */
export const LordIconRow = memo(({
  colors,
  rowColor = "#AAAAAA", // Default row hover color
  rowStroke,
  src,
  size,
  trigger,
  delay,
  stroke,
  isHover: externalIsHover,
  isRowHover,
  target,
  disabled = false,
  mainColor,
  style,
  ...rest
}: LordIconRowProps) => {
  // Track internal hover state
  const [internalIsHover, setInternalIsHover] = useState(false);
  
  // Use either external or internal hover state
  const isHover = externalIsHover !== undefined ? externalIsHover : internalIsHover;
  
  // Determine the effective colors based on hover states
  const effectiveColors = useMemo(() => {
    if (disabled) {
      return {
        primary: "#D9D9D9",
        secondary: "#D9D9D9"
      };
    }
    
    if (isHover) {
      return {
        primary: colors?.primary || mainColor || "#274649",
        secondary: colors?.secondary || "#FFB073"
      };
    }
    
    if (isRowHover && rowColor) {
      return {
        primary: rowColor,
        secondary: rowColor
      };
    }
    
    return {
      primary: mainColor || "#274649",
      secondary: "#274649"
    };
  }, [colors, mainColor, isHover, isRowHover, rowColor, disabled]);
  
  // Determine the stroke value based on hover state
  const effectiveStroke = useMemo(() => {
    if (isRowHover && rowStroke !== undefined) {
      return rowStroke;
    }
    
    return stroke || 40;
  }, [stroke, isRowHover, rowStroke]);
  
  return (
    <div 
      onMouseEnter={() => setInternalIsHover(true)}
      onMouseLeave={() => setInternalIsHover(false)}
      style={{ display: 'inline-block', ...style }}
    >
      <LordIcon
        colors={{
          primary: effectiveColors.primary,
          secondary: effectiveColors.secondary
        }}
        src={src}
        size={size}
        trigger={trigger}
        delay={delay}
        stroke={effectiveStroke}
        isHover={isHover}
        target={target}
        disabled={disabled}
        mainColor={mainColor}
        {...rest}
      />
    </div>
  );
});
